import { useEffect, useState } from 'react';
import { read_cookie } from "sfcookies";
import { hostUrl } from "../../../../utils/url-manager";

/**
 * One pool: how much of it is in use, how much the plan allows, and whether one more
 * fits. `headroom` is the answer HQ actually acts on; the numbers beside it are for
 * saying why.
 */
export type AllowancePool = {
    used: number,
    allowed: number,
    headroom: boolean,
};

export type AccountAllowances = {
    customerAccountId: number,
    isSubscribed: boolean,
    companies: AllowancePool,
    domains: AllowancePool,
    seats: AllowancePool,
    /** A company that brings a domain needs room in both pools. */
    canAddCompany: boolean,
};

type UseAccountAllowances = {
    loadingAllowances: boolean,
    allowancesError: string,
    allowances: AccountAllowances|undefined,
    reloadAllowances: () => Promise<void>,
};

/**
 * What a customer account's subscription entitles it to.
 *
 * The customer sees these numbers in the portal and is refused by them there. HQ saw
 * none of them until 2026-09-10, so an admin adding a company or a seat holder had no
 * way to know the account was already full. Served by
 * `/hq/customer/account/allowances`, which reads the same `SubscriptionAllowances` the
 * portal does.
 *
 * The API refuses an over-allowance write on its own account. This hook is so the admin
 * is told before they try, not instead of that.
 */
export const useAccountAllowances: (customerAccountId: string|number|undefined) => UseAccountAllowances = (
    customerAccountId: string|number|undefined
): UseAccountAllowances => {
    const [loadingAllowances, setLoadingAllowances] = useState<boolean>(true);
    const [allowancesError, setAllowancesError] = useState<string>('');
    const [allowances, setAllowances] = useState<AccountAllowances|undefined>();

    async function reloadAllowances(): Promise<void> {
        if (customerAccountId === undefined || customerAccountId === '') {
            return;
        }
        setLoadingAllowances(true);
        const url: string = hostUrl + 'hq/customer/account/allowances?customerAccountId=' + customerAccountId;
        const token: string = read_cookie('auth');
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'token': token,
            })
        });

        return fetch(request)
            .then((response: Response): Promise<any> => response.json())
            .then((data: any): void => {
                setLoadingAllowances(false);
                if (data?.status === 'success') {
                    setAllowancesError('');
                    setAllowances(data?.data);
                    return;
                }
                setAllowancesError(data?.message ?? 'Could not read this account\'s allowances.');
            })
            .catch((error: Error): void => {
                setLoadingAllowances(false);
                setAllowancesError('Could not read this account\'s allowances.');
                console.error(error);
            });
    }

    useEffect((): void => {
        reloadAllowances();
        // The account id is the only thing that changes what is loaded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerAccountId]);

    return { loadingAllowances, allowancesError, allowances, reloadAllowances };
}
